import React from "react";
import {
  Shield,
  Lock,
  CreditCard,
  RotateCcw,
  AlertCircle,
  CheckCircle,
  Truck,
  Info,
  Heart,
  UserX,
  Gavel,
  HelpCircle,
  RefreshCw,
  Stethoscope,
  ShieldAlert,
  Gift,
  Scale,
  XCircle,
  Wallet,
  MessageSquare,
  ChevronRight,
  FileText,
  ShieldCheck,
} from "lucide-react";

const RefundPolicy = () => {
  const currentDate = new Date().toLocaleDateString("en-IN", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  return (
    <div className="min-h-screen bg-[#FFF0F3] text-gray-800 font-sans selection:bg-rose-200">
      <div className="h-2 bg-rose-500 w-full sticky top-0 z-50 shadow-md" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        {/* Header */}
        <header className="text-center mb-16">
          <div className="inline-block px-5 py-2 rounded-full bg-rose-100 text-rose-700 text-sm font-extrabold mb-6 tracking-wider uppercase shadow-sm">
            Fair & Transparent
          </div>
          <h1 className="text-4xl md:text-6xl font-extrabold text-gray-900 mb-5 tracking-tight leading-tight">
            Refund <span className="text-rose-500">Policy</span>
          </h1>
          <p className="text-xl text-gray-700 font-medium italic mb-6">
            Eveya – A brand of Farmfit Agrihealth LLP
          </p>
          <div className="flex justify-center items-center gap-6 text-sm font-medium text-gray-600 flex-wrap">
            <span className="bg-white px-4 py-1.5 rounded-full border border-rose-200 shadow-sm">
              Last Updated: January 2025
            </span>
            <span className="bg-white px-4 py-1.5 rounded-full border border-rose-200 shadow-sm text-rose-600 font-bold">
              Customer First • Zero Judgment
            </span>
          </div>
        </header>

        <div className="space-y-12">
          {/* 1. REFUND POLICY OVERVIEW */}
          <Section
            title="1. Refund Policy Overview"
            icon={<Info size={28} className="text-rose-500" />}
          >
            <div className="space-y-6">
              <div>
                <h4 className="font-bold text-gray-900">1.1 Our Commitment:</h4>
                <p>
                  At Eveya, customer satisfaction is our priority. We are
                  committed to providing high-quality menstrual health products
                  and handling all refund and replacement requests with
                  transparency and zero judgment.
                </p>
              </div>

              <div>
                <h4 className="font-bold text-gray-900 mb-3">
                  1.2 Refund Options (Choose One):
                </h4>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="p-5 bg-white border border-rose-100 rounded-xl shadow-sm hover:border-rose-300 transition-colors">
                    <p className="font-bold text-rose-600">
                      A. Full Cash Refund
                    </p>
                    <ul className="text-xs mt-2 space-y-1 text-gray-600">
                      <li>• Original payment method</li>
                      <li>• Processing: 5-7 business days</li>
                      <li>• Best for: Cash back to bank</li>
                    </ul>
                  </div>
                  <div className="p-5 bg-rose-500 text-white rounded-xl shadow-lg shadow-rose-200">
                    <p className="font-bold">B. Store Credit</p>
                    <ul className="text-xs mt-2 space-y-1 opacity-90">
                      <li>• Voucher for future orders</li>
                      <li>• Processing: Immediate</li>
                      <li>
                        • <strong>Bonus: 10% Extra Credit</strong>
                      </li>
                      <li>
                        • Best if: You plan to reorder (better value + keeps
                        credit in wallet){" "}
                      </li>
                    </ul>
                  </div>
                  <div className="p-5 bg-white border border-rose-100 rounded-xl shadow-sm hover:border-rose-300 transition-colors">
                    <p className="font-bold text-rose-600">
                      C. Product Replacement
                    </p>
                    <ul className="text-xs mt-2 space-y-1 text-gray-600">
                      <li>•	Receives the same or an alternative product of equal/greater value</li>
                      <li>•	Processing: 1-2 days to process, 3-7 days for shipment </li>
                      <li>•	Best if: product was suitable but damaged or defective</li>
                      <li>
                        •	For defective/damaged: Replacement shipped of no cost
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-green-50 p-6 rounded-2xl border border-green-100">
                  <h4 className="font-bold text-green-700 flex items-center gap-2 mb-3">
                    <CheckCircle size={18} /> 1.3 Refund Eligibility (YOU ARE
                    ELIGIBLE):
                  </h4>
                  <ul className="text-sm space-y-2 text-green-800">
                    <li>✓ Damaged/tampered packaging at delivery</li>
                    <li>
                      ✓ Defective product (Leakage, broken applicator, mold,
                      etc.)
                    </li>
                    <li>✓ Wrong product sent by us</li>
                    <li>
                      ✓ Expired/expiring product (under 3 months validity)
                    </li>
                    <li>✓ Quality issue on subscription description</li>
                    <li className="text-[11px] font-bold mt-2 border-t border-green-200 pt-2">
                      Note: Both subscribers and non-subscribers are eligible
                      for damage/defect claims.
                    </li>
                  </ul>
                </div>
                <div className="bg-red-50 p-6 rounded-2xl border border-red-100">
                  <h4 className="font-bold text-red-700 flex items-center gap-2 mb-3">
                    <XCircle size={18} /> YOU ARE NOT ELIGIBLE:
                  </h4>
                  <ul className="text-sm space-y-2 text-red-800">
                    <li>✗ Product was used and you disliked it (subjective)</li>
                    <li>
                      ✗ Personal allergic/sensitive reaction (user
                      responsibility)
                    </li>
                    <li>✗ Packaging opened by you and resealed</li>
                    <li>✗ Request exceeds refund timeline</li>
                    <li>✗ Marked "Non-Returnable" (Promotional/Clearance)</li>
                    <li>
                      ✗ Non-Subscribers: Return requested for reasons other
                      than damage/defect Subscribers have broader return
                      eligibility (see Section 3.1)
                    </li>
                  </ul>
                </div>
              </div>

              <div>
                <h4 className="font-bold text-gray-900 mb-4">
                  1.4 Refund Timeline:
                </h4>
                <div className="overflow-x-auto rounded-xl border border-rose-100 shadow-sm">
                  <table className="w-full text-sm text-left border-collapse bg-white">
                    <thead className="bg-rose-500 text-white font-bold uppercase text-[10px] tracking-widest">
                      <tr>
                        <th className="p-4 border-r border-rose-400">
                          Scenario
                        </th>
                        <th className="p-4 border-r border-rose-400">
                          Timeline
                        </th>
                        <th className="p-4">Notes</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-rose-50">
                      <tr>
                        <td className="p-4 border-r border-rose-50 font-bold">
                          Damaged/Defective/Wrong item
                        </td>
                        <td className="p-4 border-r border-rose-50">
                          7 days from delivery
                        </td>
                        <td className="p-4">
                          Both subscribers and non-subscribers eligible for
                          refund or replacement
                        </td>
                      </tr>
                      <tr>
                        <td className="p-4 border-r border-rose-50 font-bold">
                          Unopened/Unused products (Subscribers only)
                        </td>
                        <td className="p-4 border-r border-rose-50">
                          30 days from delivery
                        </td>
                        <td className="p-4">
                          Non-subscribers not eligible for unopened returns
                        </td>
                      </tr>
                      <tr>
                        <td className="p-4 border-r border-rose-50 font-bold">
                          Opened but unused (Subscribers only)
                        </td>
                        <td className="p-4 border-r border-rose-50">
                          15 days from delivery
                        </td>
                        <td className="p-4">
                          Non-subscribers not eligible for unopened returns
                        </td>
                      </tr>
                      <tr>
                        <td className="p-4 border-r border-rose-50 font-bold">
                          Expired/Expiring products
                        </td>
                        <td className="p-4 border-r border-rose-50">
                          7 days from delivery
                        </td>
                        <td className="p-4">
                          Eligible for refund or replacement
                        </td>
                      </tr>
                      <tr className="bg-rose-50/50">
                        <td className="p-4 border-r border-rose-50 font-bold">
                          Special Case: Pregnancy/Nursing
                        </td>
                        <td className="p-4 border-r border-rose-50 font-bold text-rose-600">
                          60 days from delivery
                        </td>
                        <td className="p-4 font-medium italic">
                          Requires doctor's note or proof; health exception
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div className="mt-4 p-4 bg-gray-50 rounded-xl text-xs space-y-2 text-gray-600 border border-gray-100">
                  <p>
                    <strong>Note:</strong> After these windows, refunds are at
                    our discretion and may be declined. For subscribers, we
                    offer longer timelines to encourage sustainability and
                    flexibility.
                  </p>
                  <p>
                    <strong>Special Case:</strong> Pregnant/nursing customers
                    can request refunds within 60 days (health exception) -
                    provide doctor's note and test reports.
                  </p>
                </div>
              </div>
            </div>
          </Section>

          {/* 2. NON-SUBSCRIBERS */}
          <Section
            title="2. Non-Subscribers (One-Time Purchase) Policy"
            icon={<UserX size={28} className="text-rose-500" />}
          >
            <div className="space-y-4">
              <p className="font-bold text-gray-900">
                2.1 Eligibility for Non-Subscribers:
              </p>
              <p className="text-sm">
                Non-subscribers can request refund or replacement ONLY for:
                Damaged/defective products, Wrong item shipped, or Expired
                products. They CANNOT request returns for "change of mind or
                subjective preferences" or Product unopened/unused but doesn't
                match expectations personal reasons.
              </p>

              <div className="bg-white border border-rose-100 rounded-2xl p-6 shadow-sm">
                <p className="font-bold text-rose-600 mb-4">
                  2.2 Non-Subscribers Refund Process:
                </p>
                <div className="grid md:grid-cols-5 gap-4">
                  {[
                    {
                      s: "Step 1",
                      t: "Initiate Request",
                      d: "Email support@eveya.in with Order#",
                    },
                    {
                      s: "Step 2",
                      t: "Documentation",
                      d: "Send photos/unboxing video of damage",
                    },
                    {
                      s: "Step 3",
                      t: "Choose Option",
                      d: "Cash, Credit (+10%), or Replacement",
                    },
                    {
                      s: "Step 4",
                      t: "Processing",
                      d: "Cash: 5-7 days | Credit: Immediate",
                    },
                    {
                      s: "Step 5",
                      t: "No Return Req.",
                      d: "Dispose of damaged product locally",
                    },
                  ].map((item, i) => (
                    <div key={i} className="text-center">
                      <div className="w-8 h-8 bg-rose-500 text-white rounded-full flex items-center justify-center mx-auto mb-2 text-xs font-bold">
                        {i + 1}
                      </div>
                      <p className="text-[10px] font-bold uppercase text-gray-500">
                        {item.s}
                      </p>
                      <p className="text-[11px] font-bold text-gray-900 leading-tight my-1">
                        {item.t}
                      </p>
                      <p className="text-[10px] text-gray-500 leading-tight">
                        {item.d}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
              <p className="font-bold text-gray-900">
                2.3 Timeline for non-subscribers
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center mt-4">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-[10px] font-bold">Documentation</p>
                  <p className="text-xs">Within 48 hours</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-[10px] font-bold">Approved Decision</p>
                  <p className="text-xs">Within 24 hours</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-[10px] font-bold">Cash Refund</p>
                  <p className="text-xs">5-7 Business Days</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-[10px] font-bold">Replacement</p>
                  <p className="text-xs">2-3 Business Days</p>
                </div>
              </div>
            </div>
          </Section>

          {/* 3. SUBSCRIPTION BOX RETURNS */}
          <Section
            title="3. Subscription Box returns & Cancellation"
            icon={<RefreshCw size={28} className="text-rose-500" />}
          >
            <div className="space-y-6">
              <div className="bg-rose-50 p-5 rounded-2xl border-l-4 border-rose-500">
                <h4 className="font-bold text-rose-700 mb-2">
                  3.1 Subscription Return & Placement
                </h4>
                <p className="text-sm italic mb-3">
                  Why Subscribers Get Different Terms: Subscribers receive bulk
                  quantities and have access to flexibility options that
                  non-subscribers don't.
                </p>
                <ul className="text-xs space-y-1 font-medium">
                  <li>
                    • Damaged/Defective: 7 days from delivery (refund or
                    replacement)
                  </li>
                  <li>
                    • Unopened boxes: 30 days | Opened but unused: 15 days
                  </li>
                  <li>
                    • Used product: No refund; exchange for different product
                    variant offered.
                  </li>
                </ul>
                <p className="text-xs mt-2">
                  Note on Used Products: If you used the product but want to
                  exchange for a different product variant, we can offer
                  exchange (not cash refund) because we cannot resell opened
                  menstrual products for health/hygiene reasons
                </p>
              </div>

              <div>
                <h4 className="font-bold text-gray-900 mb-2">
                  3.2 Cancellation, Skipping, Pausing and Modifications
                </h4>
                <div className="bg-amber-50 p-5 rounded-xl border border-amber-200">
                  <p className="text-sm font-bold text-amber-800 mb-2 underline uppercase tracking-wider">
                    Important:
                  </p>
                  <ul className="text-sm space-y-2 text-amber-900">
                    <li>
                      <strong>Cancellation:</strong>{" "}
                      <li>
                        • Subscription can be cancelled only at the end of its
                        current billing cycle
                      </li>
                      <li> • No refunds for already-paid cycles </li>
                      <li>
                        • Cancellation becomes effective on the next renewal
                        date{" "}
                      </li>
                      • Example: If your monthly subscription renews on the 15th
                      and you cancel on the 10th, no charge on the 15th. If you
                      cancel on the 16th, your current month's charge stands;
                      cancellation effective next month.
                    </li>
                    <li>
                      <strong>Skipping, Pausing, or Modifying:</strong>{" "}
                      <span className="font-black text-red-600">
                        NOT PERMITTED
                      </span>{" "}
                      – Once activated, subscription cannot be skipped, paused,
                      or modified.
                    </li>
                    <li>
                      • Subscription must run to completion of its billing cycle
                    </li>
                    <li>
                      • <strong>Modifications:</strong> No changes to frequency,
                      product assortment, or delivery address mid-cycle.
                    </li>

                    <p>
                      Exception: If you experience service failure (missed
                      deliveries, consistently wrong products), contact support
                      immediately for assistance.
                    </p>
                  </ul>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="p-4 bg-white border border-rose-100 rounded-xl">
                  <p className="font-bold text-xs mb-2">
                    Refund for Cancellation:
                  </p>
                  <ul className="text-[11px] space-y-1 text-gray-600">
                    <li>
                      • BEFORE renewal charge: No refund needed (no charge
                      applied)
                    </li>
                    <li>
                      • Within 24 hours of renewal charge: Full refund to
                      payment method (5-7 days processing)
                    </li>
                    <li>• 2-7 days after: Full refund (if not shipped)</li>
                    <li>• After shipping: Return required for refund</li>
                    <li>• 15+ days after delivery: No refund</li>
                  </ul>
                </div>
                <div className="p-4 bg-gray-900 text-white rounded-xl flex items-center justify-center">
                  <div className="text-center">
                    <p className="font-black text-rose-400 text-sm uppercase tracking-tighter">
                      No Penalties
                    </p>
                    <p className="text-[10px] opacity-70">
                      No cancellation fees • NO early termination charges • NO
                      long-term commitment requirements • Cancellation is always
                      FREE • You can re-subscribe anytime at the same terms
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-rose-50 p-5 rounded-2xl border-l-4 border-rose-500">
                <h4 className="font-bold text-rose-700 mb-2">
                  3.3 Applicability:
                </h4>
                <p className="text-sm italic mb-3">
                  This subscription policy applies to:
                </p>
                <ul className="text-xs space-y-1 font-medium">
                  <li>
                    • D2C subscriptions – Individual customer subscriptions
                  </li>
                  <li>
                    • B2B/Corporate subscriptions – Organizations using Eveya
                    subscription platform will be treated as regular subscribers
                    with full subscriber benefits and terms
                  </li>
                </ul>
              </div>
            </div>
          </Section>

          {/* 4. SAKHI SPONSORSHIP */}
          <Section
            title="4. Eveya Sakhi Sponsorship Program Policy"
            icon={<Heart size={28} className="text-rose-500" />}
          >
            <div className="bg-rose-500 text-white p-8 rounded-3xl shadow-xl relative overflow-hidden">
              <div className="relative z-10">
                <h4 className="font-bold text-xl mb-4">
                  4.1 Non-Refundable & Non-Exchangeable Terms
                </h4>
                <p className="opacity-90 text-sm mb-4">
                  The Eveya Sakhi Sponsorship Program is a special initiative to
                  support menstrual health awareness and access. Due to the
                  nature of this program:
                </p>
                <div className="grid md:grid-cols-2 gap-4 mb-6">
                  <div className="bg-white/10 p-4 rounded-xl border border-white/20">
                    <p className="font-bold text-sm underline mb-2">
                      Refund Policy:
                    </p>
                    <ul className="text-xs space-y-1 opacity-90">
                      <li>
                        • ✗ Once payment is made, refunds are NOT available
                        under any circumstance
                      </li>
                      <li>• ✗ Amounts paid are final and non-refundable</li>
                    </ul>
                  </div>
                  <div className="bg-white/10 p-4 rounded-xl border border-white/20">
                    <p className="font-bold text-sm underline mb-2">
                      Exchange/Return Policy:
                    </p>
                    <ul className="text-xs space-y-1 opacity-90">
                      <li>
                        • ✗ Once products are shipped/delivered, items cannot be
                        exchanged or returned
                      </li>
                      <li>• ✗ No refunds for products received</li>
                    </ul>
                  </div>
                </div>

                <div className="bg-white/20 p-5 rounded-xl backdrop-blur-sm border border-white/30 mb-6">
                  <h4 className="font-bold flex items-center gap-2 mb-2">
                    <AlertCircle size={18} /> 4.2 Damaged Product Exception:
                  </h4>
                  <p className="text-sm opacity-90">
                    ONLY Exception: If a product arrives damaged during transit:
                  </p>
                  <ul className="text-xs mt-2 space-y-1 list-disc pl-5 opacity-90">
                    <li>
                      We will send a replacement of the same product at no cost
                    </li>
                    <li>No refund offered; replacement product sent only</li>
                    <li>
                      Damage must be reported within 7 days of delivery with
                      photographic evidence
                    </li>
                  </ul>
                  <p className="text-[10px] mt-3 italic font-bold">
                    Note: This is only applicable if the user/sponsor opts for
                    door delivery of sakhi products.
                  </p>
                </div>

                <div className="space-y-4">
                  <h4 className="font-bold text-lg border-b border-white/20 pb-1">
                    4.3 No modification or cancellations:
                  </h4>
                  <p className="text-sm opacity-90">
                    Once a Sakhi sponsorship is activated and payment is
                    received:
                  </p>
                  <ul className="grid grid-cols-2 gap-2 text-[11px] font-bold">
                    <li className="flex items-center gap-2">
                      ✗ No Cancellation
                    </li>
                    <li className="flex items-center gap-2">
                      ✗ No Assortment Modification
                    </li>
                    <li className="flex items-center gap-2">
                      ✗ No Timing Change
                    </li>
                    <li className="flex items-center gap-2">
                      ✗ No Quantity Adjustment
                    </li>
                  </ul>
                  <div className="bg-black/10 p-3 rounded-lg mt-4">
                    <p className="text-xs leading-relaxed">
                      <span className="font-bold">Why?</span> Sakhi sponsorships
                      are designed to ensure consistent support for
                      beneficiaries. Changes mid-program disrupts the intended
                      impact.
                    </p>
                  </div>
                </div>
              </div>
              <Heart className="absolute -bottom-10 -right-10 text-white/5 w-64 h-64 rotate-12" />
            </div>
          </Section>

          {/* 5. DEFECTIVE PRODUCT CLAIMS */}
          <Section
            title="5. Defective Product Claims"
            icon={<AlertCircle size={28} className="text-rose-500" />}
          >
            <div className="space-y-10">
              {/* 5.1 What Qualifies */}
              <div className="grid md:grid-cols-2 gap-8">
                <div className="space-y-6">
                  <div>
                    <h4 className="font-bold text-gray-900 mb-3 text-sm">
                      5.1 What qualifies as defective:
                    </h4>
                    <div className="space-y-4">
                      <div>
                        <p className="text-[10px] font-bold text-rose-600 uppercase tracking-wider mb-2">
                          Manufacturing issues:
                        </p>
                        <ul className="text-xs text-gray-600 space-y-1 font-medium">
                          <li>✓ Leaking or damaged applicators (pads)</li>
                          <li>
                            ✓ Damaged tampons (shredded, torn string, broken
                            applicator)
                          </li>
                          <li>✓ Broken applicator mechanics</li>
                          <li>✓ Visible mold or contamination</li>
                          <li>
                            ✓ Foul odor (chemical/mold smell, not normal product
                            smell)
                          </li>
                        </ul>
                      </div>
                      <div>
                        <p className="text-[10px] font-bold text-rose-600 uppercase tracking-wider mb-2">
                          Quality, Packing & Shipping:
                        </p>
                        <ul className="text-xs text-gray-600 space-y-1 font-medium">
                          <li>
                            ✓ Product expired or expiring in less than 3 months
                          </li>
                          <li>✓ Damaged packaging affecting seal/sterility</li>
                          <li>✓ Package arrived damaged or opened</li>
                          <li>✓ Contents leaked onto packaging</li>
                          <li>✓ Wrong product shipped</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-red-50 p-6 rounded-2xl border border-red-100 h-fit">
                  <h4 className="font-bold text-red-700 text-xs uppercase mb-3 flex items-center gap-2">
                    <XCircle size={16} /> Does NOT Qualify:
                  </h4>
                  <ul className="text-xs text-red-800 space-y-2 font-medium">
                    <li>
                      ✗ You didn't like the product (subjective preference)
                    </li>
                    <li>✗ Different thickness/absorption than expected</li>
                    <li>
                      ✗ Personal allergic/sensitive reaction (your skin
                      sensitivity)
                    </li>
                    <li>✗ Packaging opened by you and resealed</li>
                  </ul>
                </div>
              </div>

              {/* 5.2 Defect Claim Process */}
              <div className="bg-white border border-rose-100 rounded-3xl p-6 md:p-8 shadow-sm">
                <h4 className="font-bold text-gray-900 mb-6 text-sm underline decoration-rose-300 underline-offset-4">
                  5.2 Defect Claim Process:
                </h4>
                <div className="grid md:grid-cols-5 gap-4">
                  {[
                    {
                      s: "Step 1",
                      t: "Report (7 Days)",
                      d: "Email support@eveya.in with 'Defective Product - [Order#]'",
                    },
                    {
                      s: "Step 2",
                      t: "Provide Evidence",
                      d: "Photos of defect, barcode/batch number, and expiry date",
                    },
                    {
                      s: "Step 3",
                      t: "Assessment",
                      d: "We review within 24-48 hours and approve or request info",
                    },
                    {
                      s: "Step 4",
                      t: "Return (If Req.)",
                      d: "Ship using provided label; include order number note",
                    },
                    {
                      s: "Step 5",
                      t: "Resolution",
                      d: "Full refund (5-7 days) or free same-day replacement shipping",
                    },
                  ].map((item, i) => (
                    <div key={i} className="relative text-center group">
                      <div className="w-8 h-8 bg-rose-500 text-white rounded-full flex items-center justify-center mx-auto mb-3 text-xs font-bold shadow-md group-hover:scale-110 transition-transform">
                        {i + 1}
                      </div>
                      <p className="text-[10px] font-bold text-gray-400 uppercase mb-1">
                        {item.s}
                      </p>
                      <p className="text-[11px] font-bold text-gray-900 leading-tight mb-2">
                        {item.t}
                      </p>
                      <p className="text-[10px] text-gray-500 leading-relaxed px-2">
                        {item.d}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* 5.3 & 5.4 & 5.5 */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-6">
                  <div className="p-5 bg-rose-50 rounded-2xl border border-rose-100">
                    <h4 className="font-bold text-rose-700 text-xs uppercase mb-2">
                      5.3 Batch Issues:
                    </h4>
                    <p className="text-[11px] text-rose-900 leading-relaxed font-medium">
                      If multiple customers report defects from the same batch,
                      we investigate. We may issue a recall and offer free
                      replacements to all affected customers, and notify
                      relevant authorities if health/safety risks exist.
                    </p>
                  </div>
                  <div className="p-5 bg-gray-900 text-white rounded-2xl">
                    <h4 className="font-bold text-rose-400 text-xs uppercase mb-2">
                      5.5 Quality Guarantee:
                    </h4>
                    <p className="text-[11px] opacity-90 leading-relaxed">
                      We stand behind our products. If genuinely defective, we
                      make it right (refund or replacement) with zero judgment.
                      Prompt resolution builds the trust we value.
                    </p>
                  </div>
                </div>

                <div className="bg-amber-50 p-6 rounded-2xl border border-amber-200">
                  <h4 className="font-bold text-amber-900 text-sm flex items-center gap-2 mb-3">
                    <Stethoscope size={20} /> 5.4 Product
                    Allergies/Sensitivities:
                  </h4>
                  <p className="text-xs leading-relaxed text-amber-900 mb-4">
                    Menstrual products contain various materials (cotton, rayon,
                    synthetic). If you experience itching, rashes, or
                    irritation:
                  </p>
                  <ul className="text-[11px] text-amber-800 space-y-2 mb-4 font-bold">
                    <li className="flex items-center gap-2">
                      • Stop using immediately – Discontinue use
                    </li>
                    <li className="flex items-center gap-2">
                      • Consult a doctor – Seek medical advice
                    </li>
                    <li className="flex items-center gap-2">
                      • Note: This is NOT a manufacturing defect
                    </li>
                  </ul>
                  <p className="text-[11px] text-amber-900 italic font-medium p-3 bg-white/50 rounded-lg border border-amber-100">
                    <strong>Our offer:</strong> Exchange for a hypoallergenic
                    alternative product. We recommend first-time users try a
                    small quantity before committing to a subscription.
                  </p>
                </div>
              </div>
            </div>
          </Section>

          {/* 6. REFUND PROCESSING & TIMELINE (MATCHING IMAGE 1) */}
          <Section
            title="6. Refund Processing & Timeline"
            icon={<Truck size={28} className="text-rose-500" />}
          >
            <h4 className="font-bold text-gray-900 mb-4">
              6.1 Refund Processing Steps:
            </h4>
            <div className="overflow-x-auto rounded-2xl border border-rose-100 shadow-sm mb-6">
              <table className="w-full text-sm text-left border-collapse bg-white">
                <thead className="bg-gray-800 text-white font-bold uppercase text-[10px] tracking-widest">
                  <tr>
                    <th className="p-4 border-r border-gray-700">Phase</th>
                    <th className="p-4 border-r border-gray-700">Duration</th>
                    <th className="p-4">Details</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-rose-100">
                  <tr>
                    <td className="p-4 border-r border-rose-50 font-bold">
                      Approval
                    </td>
                    <td className="p-4 border-r border-rose-50">Day 1</td>
                    <td className="p-4">
                      Return request verified; eligibility confirmed
                    </td>
                  </tr>
                  <tr>
                    <td className="p-4 border-r border-rose-50 font-bold">
                      Transit to Warehouse
                    </td>
                    <td className="p-4 border-r border-rose-50">Days 2-7</td>
                    <td className="p-4">
                      You ship using provided prepaid label (if applicable); 3-7
                      days depending on location
                    </td>
                  </tr>
                  <tr>
                    <td className="p-4 border-r border-rose-50 font-bold">
                      Inspection
                    </td>
                    <td className="p-4 border-r border-rose-50">Days 8-9</td>
                    <td className="p-4">
                      Product received at warehouse; inspected within 48 hours
                    </td>
                  </tr>
                  <tr>
                    <td className="p-4 border-r border-rose-50 font-bold">
                      Refund Initiation
                    </td>
                    <td className="p-4 border-r border-rose-50 font-bold text-rose-600">
                      Days 10-12
                    </td>
                    <td className="p-4">
                      Refund approved and processed through payment gateway{" "}
                      <strong>(Zoho, PhonePe)</strong>
                    </td>
                  </tr>
                  <tr>
                    <td className="p-4 border-r border-rose-50 font-bold">
                      Bank Processing
                    </td>
                    <td className="p-4 border-r border-rose-50">Days 13-20</td>
                    <td className="p-4">
                      Bank transfer 5-7 business days (beyond our control)
                    </td>
                  </tr>
                  <tr className="bg-rose-50/50">
                    <td className="p-4 border-r border-rose-50 font-bold uppercase tracking-tighter text-gray-900">
                      Money Received
                    </td>
                    <td className="p-4 border-r border-rose-50 font-bold text-green-700">
                      Day 20
                    </td>
                    <td className="p-4 font-semibold italic">
                      Refund appears in your original payment method
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-center font-bold text-gray-900 text-sm mb-6">
              Total Timeline: 20-25 days (worst case)
            </p>

            {/* Expanded 6.2 - 6.5 Section */}
            <div className="grid md:grid-cols-2 gap-6 mt-8">
              {/* 6.2 Refund Method & 6.3 Partial Refunds */}
              <div className="space-y-6">
                <div className="p-6 bg-white border border-rose-100 rounded-2xl shadow-sm">
                  <h4 className="font-bold text-gray-900 text-sm flex items-center gap-2 mb-3">
                    <CreditCard size={18} className="text-rose-500" /> 6.2
                    Refund Method
                  </h4>
                  <ul className="text-xs text-gray-600 space-y-2 leading-relaxed">
                    <li>
                      • Refunds are issued to the{" "}
                      <strong>original payment method</strong> used for
                      purchase.
                    </li>
                    <li>
                      • If the original method no longer exists (closed
                      card/account):
                      <span className="block mt-1 pl-3 border-l-2 border-rose-100 italic">
                        Contact support for bank transfer; provide updated bank
                        details.
                      </span>
                    </li>
                    <li className="font-bold text-gray-800">
                      • No cash refunds – Digital or bank transfer only.
                    </li>
                  </ul>
                </div>

                <div className="p-6 bg-white border border-rose-100 rounded-2xl shadow-sm">
                  <h4 className="font-bold text-gray-900 text-sm flex items-center gap-2 mb-3">
                    <Scale size={18} className="text-rose-500" /> 6.3 Partial
                    Refunds
                  </h4>
                  <ul className="text-xs text-gray-600 space-y-2 leading-relaxed">
                    <li>
                      • <strong>Restocking fee:</strong> If applicable, this
                      will be deducted from the full refund.
                    </li>
                    <li>
                      • <strong>Shipping cost (Our Error):</strong> Fully
                      included in your refund.
                    </li>
                    <li>
                      • <strong>Shipping cost (Your Choice):</strong> NOT
                      included (standard e-commerce practice).
                    </li>
                    <li className="p-2 bg-rose-50 rounded text-rose-700 font-medium italic">
                      You will be informed of any deductions before the refund
                      is processed.
                    </li>
                  </ul>
                </div>
              </div>

              {/* 6.4 Failed Refunds & 6.5 Tracking Status */}
              <div className="space-y-6">
                <div className="p-6 bg-rose-50 border border-rose-100 rounded-2xl shadow-sm">
                  <h4 className="font-bold text-rose-700 text-sm flex items-center gap-2 mb-3">
                    <AlertCircle size={18} /> 6.4 Failed Refunds
                  </h4>
                  <p className="text-xs text-rose-900 mb-3 leading-relaxed">
                    If a refund fails due to closed cards or account issues, we
                    will contact you immediately with two options:
                  </p>
                  <div className="grid grid-cols-1 gap-2">
                    <div className="bg-white p-3 rounded-lg border border-rose-200">
                      <p className="font-bold text-[11px] text-gray-900">
                        Option 1: Store Credit
                      </p>
                      <p className="text-[10px] text-gray-500">
                        Full refund as store credit with a 10% bonus.
                      </p>
                    </div>
                    <div className="bg-white p-3 rounded-lg border border-rose-200">
                      <p className="font-bold text-[11px] text-gray-900">
                        Option 2: Bank Transfer
                      </p>
                      <p className="text-[10px] text-gray-500">
                        Direct transfer to your updated bank details.
                      </p>
                    </div>
                  </div>
                  <p className="text-[10px] text-rose-600 font-bold mt-3 italic text-center">
                    No refund will be lost; alternative methods are always
                    provided.
                  </p>
                </div>

                <div className="p-6 bg-gray-900 text-white rounded-2xl shadow-lg relative overflow-hidden">
                  <h4 className="font-bold text-rose-400 text-sm flex items-center gap-2 mb-4">
                    <Truck size={18} /> 6.5 Tracking Refund Status
                  </h4>
                  <div className="space-y-3 relative z-10">
                    {[
                      {
                        t: "Return Approved",
                        d: "You receive a tracking number",
                      },
                      {
                        t: "Warehouse Receipt",
                        d: "Confirmation email sent upon inspection",
                      },
                      {
                        t: "Refund Initiated",
                        d: "'Refund Processed' email with reference number",
                      },
                    ].map((step, i) => (
                      <div key={i} className="flex gap-3 items-start">
                        <div className="w-4 h-4 rounded-full bg-rose-500 flex-shrink-0 flex items-center justify-center text-[8px] font-bold mt-0.5">
                          {i + 1}
                        </div>
                        <div>
                          <p className="text-[11px] font-bold leading-none">
                            {step.t}
                          </p>
                          <p className="text-[10px] opacity-60">{step.d}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 pt-4 border-t border-white/10">
                    <p className="text-[10px] text-rose-300 font-medium italic">
                      If the refund is not received after 15 days of initiation,
                      please contact support immediately.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </Section>

          {/* Sections 7 & 8: Store Credit & Special Cases */}
          <div className="grid md:grid-cols-2 gap-8">
            {/* 7. STORE CREDIT & VOUCHERS */}
            <Section
              title="7. Store Credit & Vouchers"
              icon={<Wallet size={24} className="text-rose-500" />}
            >
              <div className="space-y-6">
                <div>
                  <h4 className="font-bold text-gray-900 text-sm mb-3">
                    7.1 Bonus Store Credit
                  </h4>
                  <div className="p-5 bg-rose-500 text-white rounded-2xl shadow-lg relative overflow-hidden">
                    <p className="text-[11px] leading-relaxed mb-3 relative z-10">
                      When you choose store credit instead of a cash refund, a{" "}
                      <strong>10% extra bonus</strong> is added to your account
                      automatically.
                    </p>
                    <div className="bg-white/20 p-3 rounded-xl text-xs font-mono mb-3 relative z-10 border border-white/30">
                      Example: Refund ₹1000 = ₹1100 store credit
                    </div>
                    <p className="text-[10px] italic opacity-90 relative z-10">
                      <strong>Advantage:</strong> Encourages repeat purchases,
                      offers better value, and keeps your funds ready in your
                      Eveya account.
                    </p>
                    <Wallet className="absolute -bottom-4 -right-4 text-white/10 w-24 h-24 rotate-12" />
                  </div>
                </div>

                <div className="space-y-3">
                  <h4 className="font-bold text-gray-900 text-xs uppercase tracking-widest border-b border-rose-100 pb-1">
                    7.2 Voucher Terms
                  </h4>
                  <ul className="text-[11px] text-gray-600 space-y-3 font-medium">
                    <li className="flex items-start gap-2">
                      <CheckCircle
                        size={14}
                        className="text-rose-400 shrink-0 mt-0.5"
                      />
                      <span>
                        Store credit vouchers are redeemable on{" "}
                        <strong>any product</strong> on our platform.
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle
                        size={14}
                        className="text-rose-400 shrink-0 mt-0.5"
                      />
                      <span>
                        <strong>No expiry date:</strong> Vouchers are valid
                        indefinitely until used.
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle
                        size={14}
                        className="text-rose-400 shrink-0 mt-0.5"
                      />
                      <span>
                        Non-transferable: Credits are personal to your
                        registered account.
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle
                        size={14}
                        className="text-rose-400 shrink-0 mt-0.5"
                      />
                      <span>
                        Can be combined with other offers, discounts, or
                        promotions.
                      </span>
                    </li>
                    <li className="flex items-start gap-2 p-2 bg-red-50 rounded-lg text-red-700">
                      <XCircle size={14} className="shrink-0 mt-0.5" />
                      <span>
                        <strong>Cannot be converted back to cash</strong> once
                        the voucher is issued.
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </Section>

            {/* 8. SPECIAL CASES AND EXCEPTIONS */}
            <Section
              title="8. Special Cases & Exceptions"
              icon={<Gift size={24} className="text-rose-500" />}
            >
              <div className="space-y-5">
                {/* 8.1 Medical */}
                <div className="p-4 bg-amber-50 border border-amber-100 rounded-2xl">
                  <h4 className="font-bold text-amber-900 text-xs flex items-center gap-2 mb-2">
                    <Stethoscope size={16} /> 8.1 Medical/Health Exceptions
                  </h4>
                  <p className="text-[11px] text-amber-800 font-bold mb-2">
                    Extended Refund Window: 60 days from delivery
                  </p>
                  <ul className="text-[10px] text-amber-800 space-y-1 mb-2 list-disc pl-4">
                    <li>Pregnant customers (requires doctor's note)</li>
                    <li>Nursing/breastfeeding customers (requires proof)</li>
                    <li>
                      Medical conditions preventing use (requires documentation)
                    </li>
                  </ul>
                  <p className="text-[10px] italic text-amber-700 bg-white/50 p-2 rounded border border-amber-100">
                    <strong>Why?</strong> Pregnancy and nursing change menstrual
                    and health needs; we accommodate these transitions.
                  </p>
                </div>

                {/* 8.2 Harassment */}
                <div className="p-4 bg-rose-50 border border-rose-100 rounded-2xl">
                  <h4 className="font-bold text-rose-900 text-xs flex items-center gap-2 mb-2">
                    <ShieldAlert size={16} /> 8.2 Harassment/Safety Concerns
                  </h4>
                  <p className="text-[10px] text-rose-800 leading-relaxed mb-2">
                    If you experience harassment, safety concerns, or privacy
                    issues when receiving products:
                  </p>
                  <ul className="text-[10px] font-bold text-rose-700 space-y-1 mb-3">
                    <li>• Immediate cancellation without penalty</li>
                    <li>• Full refund for any pending charges</li>
                    <li>• Zero tolerance for judgment-based treatment</li>
                  </ul>
                  <p className="text-[9px] font-bold text-rose-900 uppercase tracking-tighter mb-1">
                    Alternative Shipping Available:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {["Office Address", "Friend/Family", "Secure Delivery"].map(
                      (opt) => (
                        <span
                          key={opt}
                          className="px-2 py-0.5 bg-white border border-rose-200 rounded-full text-[9px] text-rose-600"
                        >
                          {opt}
                        </span>
                      ),
                    )}
                  </div>
                </div>

                {/* 8.3, 8.4, 8.5 Small Detailed Items */}
                <div className="space-y-4 px-1">
                  <div>
                    <h5 className="font-bold text-gray-900 text-[11px] mb-1">
                      8.3 Price Errors:
                    </h5>
                    <p className="text-[10px] text-gray-600 leading-relaxed">
                      We reserve the right to correct pricing errors before
                      charging. If charged due to our error, a full refund +
                      apology is issued. No retroactive discounts for price
                      reductions after purchase.
                    </p>
                  </div>

                  <div className="pt-3 border-t border-rose-50">
                    <h5 className="font-bold text-gray-900 text-[11px] mb-2">
                      8.4 Promotional & Sale Items:
                    </h5>
                    <div className="grid grid-cols-2 gap-3 text-[10px]">
                      <div className="space-y-1">
                        <p className="text-red-600 font-bold">
                          • Final Sale Items:
                        </p>
                        <p className="text-gray-500">
                          No returns/refunds accepted.
                        </p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-gray-800 font-bold">
                          • Clearance Items:
                        </p>
                        <p className="text-gray-500">
                          Reduced 15-day return window.
                        </p>
                      </div>
                    </div>
                    <p className="text-[9px] text-gray-400 mt-2 italic">
                      Flash sale items follow standard policy unless marked
                      "Non-Returnable".
                    </p>
                  </div>

                  <div className="pt-3 border-t border-rose-50">
                    <h5 className="font-bold text-gray-900 text-[11px] mb-1">
                      8.5 Gifted Products:
                    </h5>
                    <p className="text-[10px] text-gray-600 leading-relaxed">
                      Gifts can be returned/exchanged within{" "}
                      <strong>30 days</strong>. Receipt not required; only
                      Order# or email verification needed.
                      <span className="block mt-1 font-medium italic">
                        Refund goes to original payment method, or recipient can
                        choose store credit.
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </Section>
          </div>

          {/* 9. CHARGEBACK & DISPUTE POLICY */}
          <Section
            title="9. Chargeback & Dispute Policy"
            icon={<Gavel size={28} className="text-rose-500" />}
          >
            <div className="space-y-8">
              {/* 9.1 Important Warning */}
              <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-2xl">
                <h4 className="font-bold text-red-700 text-sm mb-2 uppercase tracking-wider">
                  9.1 Important: Chargebacks vs. Refunds
                </h4>
                <p className="text-sm font-black text-red-600 underline mb-3">
                  ALWAYS CONTACT US FIRST BEFORE INITIATING A CHARGEBACK.
                </p>
                <p className="text-xs text-red-800 leading-relaxed">
                  A chargeback is when you ask your bank to reverse a charge (claiming fraudulent/unauthorized transaction). This is different from our refund process. Contacting your bank first disrupts the resolution, flags your account as risky, and takes 60+ days (we refund in 7-12 days).
                </p>
              </div>

              {/* 9.2 Payment Disputes Process */}
              <div className="space-y-4">
                <h4 className="font-bold text-gray-900 text-sm border-b border-rose-100 pb-1">9.2 Payment Disputes:</h4>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="p-4 bg-white border border-rose-100 rounded-xl">
                    <p className="font-bold text-xs mb-1">Step 1: Contact Us FIRST</p>
                    <p className="text-[10px] text-gray-500 leading-relaxed">Email support@eveya.in with Order number, reason, and supporting evidence.</p>
                  </div>
                  <div className="p-4 bg-white border border-rose-100 rounded-xl">
                    <p className="font-bold text-xs mb-1">Step 2: Investigation</p>
                    <p className="text-[10px] text-gray-500 leading-relaxed">We investigate within 7 days, reviewing transaction details and responding with a resolution.</p>
                  </div>
                  <div className="p-4 bg-white border border-rose-100 rounded-xl">
                    <p className="font-bold text-xs mb-1">Step 3: Escalation</p>
                    <p className="text-[10px] text-gray-500 leading-relaxed">Only escalate to your bank if we do not respond within 7 days of your initial contact.</p>
                  </div>
                </div>
              </div>

              {/* 9.3 Evidence & 9.4 Repeated Chargebacks */}
              <div className="grid md:grid-cols-2 gap-8">
                <div className="space-y-4">
                  <h4 className="font-bold text-gray-900 text-sm">9.3 Our Dispute Evidence</h4>
                  <p className="text-xs text-gray-600 leading-relaxed">
                    We maintain detailed records to protect against unjustified claims, including Order confirmations, Payment authorizations, Delivery proofs, and Customer communication. We provide this evidence to banks for every chargeback filed.
                  </p>
                </div>

                {/* 9.4 REPEATED CHARGEBACKS (ADDED HERE) */}
                <div className="p-5 bg-gray-900 text-white rounded-2xl shadow-lg border border-gray-800">
                  <h4 className="font-bold text-rose-400 text-xs uppercase tracking-widest mb-3 flex items-center gap-2">
                    <ShieldAlert size={16} /> 9.4 Repeated Chargebacks
                  </h4>
                  <ul className="text-xs space-y-2 font-medium">
                    <li className="flex items-start gap-2">
                      <span className="text-rose-500">•</span> 
                      <span><strong>2+ chargebacks</strong> from the same customer = <strong>Immediate Account Suspension</strong></span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-rose-500">•</span> 
                      <span>We reserve the right to <strong>refuse any and all future orders</strong></span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-rose-500">•</span> 
                      <span>Your payment method and IP may be <strong>flagged as high-risk</strong> across our payment network</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* 9.5 Table Comparison */}
              <div>
                <h4 className="font-bold text-gray-900 text-sm mb-3">9.5 Refund vs. Chargeback Comparison:</h4>
                <div className="bg-white border border-rose-100 rounded-2xl overflow-hidden shadow-sm">
                  <table className="w-full text-[11px] border-collapse">
                    <thead className="bg-gray-50 text-gray-500 font-bold uppercase text-[9px] tracking-widest">
                      <tr>
                        <th className="p-4 text-left border-r border-rose-50">Method</th>
                        <th className="p-4 text-left border-r border-rose-50">Timeline</th>
                        <th className="p-4 text-left border-r border-rose-50">Cost</th>
                        <th className="p-4 text-left">Outcome</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-rose-50">
                      <tr className="bg-green-50/20">
                        <td className="p-4 font-bold text-gray-900 border-r border-rose-50">Our Refund</td>
                        <td className="p-4 border-r border-rose-50">7-12 days</td>
                        <td className="p-4 font-bold text-green-700 border-r border-rose-50">₹0</td>
                        <td className="p-4 text-green-800 font-medium">Full refund; friendly resolution</td>
                      </tr>
                      <tr className="bg-red-50/20">
                        <td className="p-4 font-bold text-gray-900 border-r border-rose-50">Chargeback</td>
                        <td className="p-4 border-r border-rose-50">60+ days</td>
                        <td className="p-4 font-bold text-red-700 border-r border-rose-50">₹500+ fee to you</td>
                        <td className="p-4 text-red-800 font-medium">Risky; may lose dispute & account</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </Section>

          {/* 10. CONTACT & ESCALATIONS */}

          <Section
            title="10. Contact and Escalations"
            icon={<HelpCircle size={28} className="text-rose-500" />}
          >
            <div className="space-y-8">
              {/* 10.1 Refund Support Channels */}
              <div>
                <h4 className="font-bold text-gray-900 text-sm mb-4">
                  10.1 Refund Support Channels:
                </h4>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="p-6 bg-white border border-rose-100 rounded-2xl shadow-sm text-center group hover:border-rose-300 transition-colors">
                    <div className="w-10 h-10 bg-rose-50 text-rose-500 rounded-full flex items-center justify-center mx-auto mb-3 group-hover:bg-rose-500 group-hover:text-white transition-colors">
                      <MessageSquare size={20} />
                    </div>
                    <p className="text-[10px] text-gray-500 font-bold uppercase mb-1">
                      Email Support
                    </p>
                    <a
                      href="mailto:support@eveya.in"
                      className="text-xl font-black text-rose-600 block hover:underline"
                    >
                      support@eveya.in
                    </a>
                    <p className="text-[10px] text-gray-400 mt-2 font-medium italic">
                      Response: Within 48 hours
                    </p>
                  </div>
                  <div className="p-6 bg-white border border-rose-100 rounded-2xl shadow-sm text-center group hover:border-rose-300 transition-colors">
                    <div className="w-10 h-10 bg-green-50 text-green-500 rounded-full flex items-center justify-center mx-auto mb-3 group-hover:bg-green-500 group-hover:text-white transition-colors">
                      <Wallet size={20} />
                    </div>
                    <p className="text-[10px] text-gray-500 font-bold uppercase mb-1">
                      WhatsApp Help
                    </p>
                    <a
                      href="tel:+918121514499"
                      className="text-xl font-black text-rose-600 block hover:underline"
                    >
                      +91 8121514499
                    </a>
                    <p className="text-[10px] text-gray-400 mt-2 font-medium italic">
                      Response: Within 24 hours
                    </p>
                  </div>
                </div>
              </div>

              {/* 10.2 Information to Provide */}
              <div className="bg-rose-50 border border-rose-100 rounded-2xl p-6">
                <h4 className="font-bold text-rose-900 text-sm mb-4 flex items-center gap-2">
                  <FileText size={18} /> 10.2 Information to Provide:
                </h4>
                <p className="text-[11px] text-rose-800 mb-4 font-medium italic">
                  Please include the following details in your claim for faster
                  processing:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
                  {[
                    "Full name and registered email address",
                    "Order number and purchase date",
                    "Specific reason for return/refund request",
                    "Preferred refund method (Cash, Wallet Credit, or Replacement)",
                    "Supporting documentation (Clear photos if damaged or defective)",
                  ].map((item, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-3 text-xs text-rose-900 font-semibold"
                    >
                      <div className="w-1.5 h-1.5 bg-rose-400 rounded-full shrink-0" />
                      {item}
                    </div>
                  ))}
                </div>
              </div>

              {/* 10.3 Escalation Timeline */}
              <div className="space-y-4">
                <h4 className="font-bold text-gray-900 text-sm">
                  10.3 Escalation Matrix:
                </h4>
                <div className="flex flex-col md:flex-row gap-4">
                  {[
                    {
                      d: "15 Days Unresolved",
                      t: "Escalate to Refund Manager",
                      icon: <UserX size={14} />,
                    },
                    {
                      d: "30 Days Unresolved",
                      t: "Escalate to Chief Customer Officer",
                      icon: <Shield size={14} />,
                    },
                    {
                      d: "Further Disputes",
                      t: "District/State Consumer Commission",
                      icon: <Gavel size={14} />,
                    },
                  ].map((item, i) => (
                    <div
                      key={i}
                      className="flex-1 p-4 bg-white rounded-xl border border-rose-100 text-center shadow-sm relative group transition-all hover:scale-105"
                    >
                      <p className="text-rose-600 font-black text-xs mb-1">
                        {item.d}
                      </p>
                      <p className="text-[10px] font-bold text-gray-900">
                        {item.t}
                      </p>
                      <div className="mt-2 flex justify-center text-rose-200 group-hover:text-rose-400 transition-colors">
                        {item.icon}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* 10.4 Consumer Complaint Escalation */}
              <div className="bg-gray-900 text-white rounded-2xl p-6 border border-gray-800">
                <div className="flex flex-col md:flex-row gap-6 items-start">
                  <div className="shrink-0 p-3 bg-white/10 rounded-xl">
                    <Scale size={24} className="text-rose-400" />
                  </div>
                  <div className="space-y-2">
                    <h4 className="font-bold text-sm text-rose-400">
                      10.4 Consumer Complaint Escalation (India)
                    </h4>
                    <p className="text-[11px] text-gray-300 leading-relaxed">
                      If you are unsatisfied with our final refund decision, you
                      have the right to file a formal complaint with the{" "}
                      <strong>
                        District Consumer Dispute Redressal Commission
                      </strong>
                      .
                    </p>
                    <ul className="text-[10px] text-gray-400 grid grid-cols-1 md:grid-cols-2 gap-2 mt-2 italic font-medium">
                      <li>• Contact your local district commission office.</li>
                      <li>
                        • Complaints must be filed within 2 years of the
                        transaction.
                      </li>
                      <li>
                        • Governed by CCPA - Consumer Protection Act, 2019.
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </Section>

          {/* 11 & 12 FINAL TERMS */}
          {/* 11. GLOBAL COMPLIANCE & 12. FINAL TERMS */}
          <div className="bg-gray-900 text-white rounded-[3rem] p-8 md:p-16 shadow-2xl overflow-hidden relative">
            {/* Background Decoration */}
            <Scale className="absolute top-10 right-10 text-rose-500/10 w-64 h-64 -rotate-12 pointer-events-none" />
            <ShieldCheck className="absolute -bottom-20 -left-20 text-white/5 w-96 h-96 pointer-events-none" />

            <div className="relative z-10 space-y-12">
              {/* Section 11: Global Compliance */}
              <div className="max-w-4xl mx-auto text-center">
                <h3 className="text-2xl md:text-3xl font-black mb-6 text-rose-500 tracking-tight">
                  11. Global Compliance & Consumer Protection
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed mb-8">
                  This policy is designed to uphold the highest standards of
                  consumer rights. All customers are protected under applicable
                  local and international consumer protection laws, regardless
                  of their location.
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { country: "India", law: "CPA, 2019" },
                    { country: "European Union", law: "Directive 2011/83/EU" },
                    { country: "United States", law: "FTC Act & State Laws" },
                    { country: "International", law: "Standard E-commerce" },
                  ].map((item, i) => (
                    <div
                      key={i}
                      className="p-3 bg-white/5 rounded-xl border border-white/10"
                    >
                      <p className="text-[10px] font-black text-rose-400 uppercase tracking-widest">
                        {item.country}
                      </p>
                      <p className="text-[10px] text-gray-300 mt-1">
                        {item.law}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Section 12: Final Terms */}
              <div className="grid md:grid-cols-2 gap-12 pt-8 border-t border-white/10">
                {/* 12.1 Policy Updates */}
                <div className="space-y-4 text-left">
                  <h4 className="text-lg font-bold text-white flex items-center gap-2">
                    <RefreshCw size={20} className="text-rose-500" /> 12.1
                    Policy Updates
                  </h4>
                  <ul className="text-xs text-gray-400 space-y-3 font-medium">
                    <li className="flex gap-2">
                      •{" "}
                      <span>
                        We may update this policy at any time to reflect
                        changing regulations.
                      </span>
                    </li>
                    <li className="flex gap-2">
                      •{" "}
                      <span>
                        Changes apply to <strong>future orders</strong>;
                        existing orders are honored under the original terms of
                        purchase.
                      </span>
                    </li>
                    <li className="flex gap-2">
                      •{" "}
                      <span>
                        Major changes will be announced via email and our
                        newsletter.
                      </span>
                    </li>
                  </ul>
                </div>

                {/* 12.2 Our Commitment */}
                <div className="space-y-4 text-left">
                  <h4 className="text-lg font-bold text-white flex items-center gap-2">
                    <Heart size={20} className="text-rose-500" /> 12.2 Our
                    Commitment
                  </h4>
                  <p className="text-xs text-rose-400 font-black uppercase tracking-tighter">
                    Goal: 92% Customer Satisfaction & Retention
                  </p>
                  <ul className="text-xs text-gray-400 space-y-3 font-medium">
                    <li className="flex gap-2">
                      •{" "}
                      <span>
                        Refunds are processed promptly, fairly, and with{" "}
                        <strong>zero judgment</strong>.
                      </span>
                    </li>
                    <li className="flex gap-2">
                      •{" "}
                      <span>
                        Your privacy is protected throughout the entire
                        resolution process.
                      </span>
                    </li>
                    <li className="flex gap-2">
                      •{" "}
                      <span>
                        We prioritize transparent communication and
                        customer-first practices.
                      </span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* 12.3 Acknowledgment */}
              <div className="pt-12 max-w-3xl mx-auto border-t border-white/10">
                <h4 className="text-lg font-bold text-white mb-4">
                  12.3 Acknowledgment
                </h4>
                <p className="text-gray-400 text-xs leading-relaxed mb-8">
                  By placing an order on our platform, you acknowledge and
                  accept this <strong>Refund Policy</strong>, our{" "}
                  <strong>Terms of Service</strong>, and our{" "}
                  <strong>Privacy Policy</strong>. These documents collectively
                  form a legally binding agreement between you and Farmfit
                  Agrihealth LLP.
                </p>

                <div className="flex flex-wrap justify-center gap-6 text-[10px] font-black uppercase tracking-[0.3em] text-rose-500">
                  <span>Wellness</span>
                  <span className="text-white/20">•</span>
                  <span>Fairness</span>
                  <span className="text-white/20">•</span>
                  <span>Transparency</span>
                  <span className="text-white/20">•</span>
                  <span>Trust</span>
                </div>

                <div className="mt-12 space-y-2">
                  <p className="text-sm font-bold text-white">
                    Questions? Contact{" "}
                    <a
                      href="mailto:support@eveya.in"
                      className="text-rose-500 hover:underline"
                    >
                      support@eveya.in
                    </a>
                  </p>
                  <p className="text-[10px] text-gray-600 font-medium">
                    © {new Date().getFullYear()} Farmfit Agrihealth LLP. All
                    Rights Reserved.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Section = ({ title, icon, children }) => (
  <section className="bg-white rounded-3xl p-6 md:p-10 border border-rose-100 shadow-xl shadow-rose-200/30 hover:shadow-rose-300/40 transition-all duration-300">
    <div className="flex items-center gap-5 mb-8">
      <div className="p-3.5 bg-rose-50 rounded-2xl shadow-inner text-rose-500">
        {icon}
      </div>
      <h2 className="text-2xl md:text-3xl font-black text-gray-900 tracking-tight">
        {title}
      </h2>
    </div>
    <div className="text-gray-700 leading-relaxed space-y-4">{children}</div>
  </section>
);

export default RefundPolicy;
